import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, SectionNum } from "@/components/ui-bits";
import api from "@/lib/api";
import { ArrowUpRight, Search } from "lucide-react";

export default function USML() {
  const [meta, setMeta] = useState(null);
  const [categories, setCategories] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [m, c] = await Promise.all([
          api.get("/usml/meta"),
          api.get("/usml/categories"),
        ]);
        setMeta(m.data);
        setCategories(c.data.categories);
      } catch { setCategories([]); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!categories) return null;
    if (!q.trim()) return categories;
    const needle = q.trim().toLowerCase();
    return categories.filter((c) =>
      c.title.toLowerCase().includes(needle) ||
      c.roman.toLowerCase().includes(needle) ||
      (c.paragraph_index || []).some((p) => (p.excerpt || "").toLowerCase().includes(needle))
    );
  }, [categories, q]);

  const lastSyncedStr = meta?.last_synced
    ? new Date(meta.last_synced).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : "—";

  return (
    <Layout>
      <SEO
        title="United States Munitions List — Navigator"
        description="Browse and search the U.S. Munitions List (22 CFR § 121.1) by category and paragraph. Full text mirrored nightly from the eCFR API."
        path="/usml"
      />

      {/* Hero */}
      <section className="border-b border-hairline" data-testid="usml-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <Eyebrow>Reference · 22 CFR § 121.1</Eyebrow>
              <h1 className="display-1 text-ink mt-6 max-w-4xl">
                United States Munitions List.
              </h1>
              <p className="mt-6 text-ink-soft italic font-serif text-lg leading-snug max-w-2xl">
                A navigator for the USML — organized by category and paragraph, searchable by keyword or Roman numeral.
              </p>
              <p className="mt-6 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
                The USML identifies defense articles and defense services subject to the International Traffic in Arms Regulations (ITAR). Each category below deep-links to its full text on the eCFR. This page is a navigational aid — verify against the eCFR before you rely on it.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right space-y-1">
              <p className="section-num" data-testid="usml-entry-count">{meta?.entry_count || "—"}</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">Categories indexed</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mt-3">Last synced {lastSyncedStr}</p>
              {meta?.source_as_of && (
                <p className="text-[10px] uppercase tracking-[0.22em] text-ink-soft">eCFR as of {meta.source_as_of}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="border-b border-hairline bg-ivory-alt" data-testid="usml-search">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="relative max-w-2xl">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" strokeWidth={2} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search categories and paragraphs (e.g. spacecraft, laser, XV)"
              className="w-full pl-11 pr-4 py-3 bg-ivory border border-hairline text-[15px] text-ink placeholder:text-ink-soft/70 focus:outline-none focus:border-brass"
              data-testid="usml-search-input"
            />
          </div>
        </div>
      </section>

      {/* Category list */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="usml-categories">
        <Eyebrow>Categories</Eyebrow>
        <h2 className="font-serif text-3xl text-ink mt-4 mb-10">Browse by category.</h2>
        {filtered === null && <p className="text-ink-soft">Loading…</p>}
        {filtered && filtered.length === 0 && <p className="text-ink-soft">No categories match &ldquo;{q}&rdquo;.</p>}
        <ul className="border-t border-hairline">
          {(filtered || []).map((c) => (
            <li key={c.roman} className="border-b border-hairline" data-testid={`usml-category-card-${c.roman}`}>
              <Link
                to={`/usml/${c.roman}`}
                className="group grid grid-cols-12 gap-6 py-8 items-baseline hover:bg-ivory-alt transition-colors -mx-3 px-3"
              >
                <div className="col-span-2 sm:col-span-1">
                  <SectionNum>{c.roman}</SectionNum>
                </div>
                <div className="col-span-8">
                  <h3 className="font-serif text-xl sm:text-2xl text-ink group-hover:text-brass transition-colors leading-tight">
                    {c.title}
                  </h3>
                  {c.paragraph_index?.length > 0 && (
                    <p className="mt-2 text-ink-soft text-[13px] leading-snug">
                      {c.paragraph_index
                        .filter((p) => !p.reserved)
                        .slice(0, 4)
                        .map((p) => `(${p.letter}) ${p.excerpt.split(",")[0].split(".")[0].slice(0, 40)}`)
                        .join(" · ")}
                      {c.paragraph_index.length > 4 ? " · …" : ""}
                    </p>
                  )}
                </div>
                <div className="col-span-2 sm:col-span-3 text-right">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">
                    {c.paragraph_count || 0} paragraph{(c.paragraph_count || 0) === 1 ? "" : "s"}
                  </span>
                  <ArrowUpRight className="w-4 h-4 inline-block ml-2 text-brass opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Disclaimer */}
      <section className="bg-ink text-ivory" data-testid="usml-disclaimer">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-14 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-4">Note</p>
          <p className="font-serif text-xl sm:text-2xl leading-snug tracking-tight">
            This navigator mirrors the USML from the public eCFR API and refreshes hourly. It is not the authoritative text and is not legal advice. Verify each category against the eCFR before relying on it.
          </p>
        </div>
      </section>
    </Layout>
  );
}
